import { getPagePathByKey, getPageKeyByPath } from '@repo/permissions';
import { operationalNotificationPath } from '@repo/contracts';
import {
  resolvePageKeyFromNotificationI18n,
  resolvePageKeyFromNotificationTitle,
} from './notification-i18n-page-map.js';

function decodeLegacyNotificationUrlEntities(value) {
  return String(value || '')
    .replace(/&amp;/gi, '&')
    .replace(/&#(?:x0*3d|0*61);/gi, '=');
}

export function normalizeNotificationActionUrl(value, origin = globalThis.location?.origin) {
  const raw = value ? decodeLegacyNotificationUrlEntities(value).trim() : '';
  if (!raw) return '';
  if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
  if (!origin) return '';

  try {
    const target = new URL(raw, origin);
    const trustedOrigin = new URL(origin).origin;
    if (target.origin !== trustedOrigin) return '';
    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return '';
  }
}

/**
 * @param {object} notification
 * @param {{ origin?: string }} [options]
 * @returns {{
 *   pageKey?: string,
 *   entityId?: string,
 *   pathname?: string,
 *   navigationKind?: string,
 *   tab?: string,
 *   linkedEntityIds?: string[],
 * } | null}
 */
export function resolveNotificationTarget(notification, options = {}) {
  if (!notification || typeof notification !== 'object') return null;

  const data = notification.data || {};
  const pageKey =
    data.pageKey ||
    notification.params?.pageKey ||
    resolvePageKeyFromNotificationI18n(
      data.i18n?.key || notification.params?.i18n?.key,
      data.category || notification.params?.category,
    ) ||
    resolvePageKeyFromNotificationTitle(notification.title) ||
    undefined;
  const entityId =
    data.entityId ||
    notification.params?.entityId ||
    undefined;
  const navigationKind = data.navigationKind;
  const tab = data.tab || notification.params?.tab;
  const linkedEntityIds = (
    data.linkedEntityIds ||
    notification.params?.linkedEntityIds ||
    []
  ).map(String).filter(Boolean);
  const actionUrl = normalizeNotificationActionUrl(
    notification.actionUrl,
    options.origin ?? globalThis.location?.origin,
  );

  let pathname;
  let resolvedPageKey = pageKey;
  let resolvedEntityId = entityId ? String(entityId) : undefined;

  if (actionUrl) {
    pathname = actionUrl.split('#')[0];
    const pathOnly = pathname.split('?')[0].replace(/\/$/, '') || pathname;
    const controlDetailMatch = pathOnly.match(/^\/isms-controls\/control\/([^/]+)$/);
    const riskDetailMatch = pathOnly.match(/^\/risk-management\/risk-register\/([^/]+)$/);
    if (controlDetailMatch) {
      resolvedPageKey = resolvedPageKey || 'internalControlControl';
      resolvedEntityId = resolvedEntityId || controlDetailMatch[1];
    } else if (riskDetailMatch) {
      resolvedPageKey = resolvedPageKey || 'riskOperationsRiskRegister';
      resolvedEntityId = resolvedEntityId || riskDetailMatch[1];
    } else {
      resolvedPageKey = resolvedPageKey || getPageKeyByPath(pathOnly);
    }
  } else if (resolvedPageKey) {
    pathname = getPagePathByKey(resolvedPageKey);
  }

  const i18nKey = data.i18n?.key || notification.params?.i18n?.key;
  if (resolvedPageKey === 'itsmProblem' && resolvedEntityId) {
    const verb = String(i18nKey || '').split('.').pop() || '';
    if (verb.startsWith('candidate')) {
      pathname = '/itsm/problem?tab=suggestions';
    } else {
      pathname = `/itsm/problem/${resolvedEntityId}`;
    }
  }
  if (resolvedPageKey === 'itsmChangeRequest' && resolvedEntityId) {
    pathname = `/itsm/change-request/${resolvedEntityId}`;
  }
  const operationalOverride = operationalNotificationPath({
    eventName: i18nKey,
    entityId: resolvedEntityId,
    sourceType: data.i18n?.params?.sourceType || notification.params?.sourceType,
    sourceId: data.i18n?.params?.sourceId || notification.params?.sourceId,
  });
  const myWorkListPath = String(getPagePathByKey('myWork') || '/my-work').replace(/\/$/, '');
  const currentPath = String(pathname || '').split('?')[0].replace(/\/$/, '');
  if (operationalOverride && (!currentPath || currentPath === myWorkListPath)) {
    pathname = operationalOverride;
  }

  if (!pathname && !resolvedPageKey && !resolvedEntityId) return null;

  const directPath = Boolean(pathname && !resolvedPageKey && !resolvedEntityId);

  return {
    pageKey: resolvedPageKey || (pathname ? getPageKeyByPath(pathname.split('?')[0]) : undefined),
    entityId: resolvedEntityId,
    pathname: pathname || (resolvedPageKey ? getPagePathByKey(resolvedPageKey) : undefined),
    navigationKind: navigationKind || (directPath ? 'direct' : undefined),
    tab: tab ? String(tab) : undefined,
    linkedEntityIds,
  };
}

/**
 * @param {ReturnType<typeof resolveNotificationTarget>} target
 */
export function canNavigateToListHighlight(target) {
  if (target?.navigationKind === 'direct') {
    return Boolean(target.pathname?.startsWith('/') && !target.pathname.startsWith('//'));
  }
  if (!target?.pageKey) return false;
  const pathname = target.pathname || getPagePathByKey(target.pageKey);
  if (!pathname) return false;
  if (target.navigationKind === 'detailTabHighlight') {
    return Boolean(target.entityId);
  }
  if (target.navigationKind === 'listPageOnly') return true;
  if (!target.entityId) return true;
  return target.navigationKind === 'listRowHighlight' || Boolean(target.entityId);
}
